import { useNavigate } from '@tanstack/react-router';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  getListInterviewInvitesQueryKey,
  listInterviewInvites,
  ListInterviewInvitesPageSize,
  type ListInterviewInvitesResponseDto,
} from '@sara/api-client';
import { Button } from '@sara/ui/components/button';
import { DataPanel } from '@sara/ui/components/data-panel';
import { Skeleton } from '@sara/ui/components/skeleton';
import { useOrgSlug } from '../../../auth';
import { CandidateInterviewListTable } from '../../components/CandidateInterviewListTable';

// Matches the API's default/maximum page size for listInterviewInvites. A
// candidate with > PAGE_SIZE invites will need to click "Show more" to load
// the next page; reducing this would just mean more clicks for the same data.
const PAGE_SIZE = ListInterviewInvitesPageSize.NUMBER_25;

interface CandidateInterviewsContainerContentProps {
  orgId: string;
  candidateId: string;
}

export function CandidateInterviewsContainerContent({
  orgId,
  candidateId,
}: CandidateInterviewsContainerContentProps) {
  const navigate = useNavigate();
  const orgSlug = useOrgSlug();

  const baseParams = {
    orgId,
    candidateId,
    sortBy: 'createdAt' as const,
    sortOrder: 'desc' as const,
    pageSize: PAGE_SIZE,
  };

  const { data, isLoading, error, hasNextPage, fetchNextPage, isFetching, isFetchingNextPage } =
    useInfiniteQuery<ListInterviewInvitesResponseDto>({
      // Reuse the generated query-key function so cache invalidation by other
      // call sites that key off listInterviewInvites still intersects with
      // this infinite query. The page param is provided per-page by react-query.
      queryKey: getListInterviewInvitesQueryKey(baseParams),
      queryFn: ({ pageParam, signal }) =>
        listInterviewInvites({ ...baseParams, page: pageParam as number }, { signal }),
      initialPageParam: 1,
      getNextPageParam: (lastPage) =>
        lastPage.page * lastPage.pageSize < lastPage.total ? lastPage.page + 1 : undefined,
    });

  if (error) throw error;

  if (isLoading) {
    return (
      <DataPanel>
        <DataPanel.Body className="space-y-px">
          <Skeleton className="h-16 rounded-none" />
          <Skeleton className="h-16 rounded-none" />
          <Skeleton className="h-16 rounded-none" />
        </DataPanel.Body>
      </DataPanel>
    );
  }

  // Render the union of all loaded pages — the "Show more" affordance must
  // append rows, never replace the previously-visible ones.
  const items = data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <CandidateInterviewListTable
      items={items}
      isFetching={isFetching && !isFetchingNextPage}
      onRowClick={(invite) =>
        void navigate({
          to: '/orgs/$orgSlug/sessions/$interviewId',
          params: { orgSlug, interviewId: invite.id },
        })
      }
      footer={
        hasNextPage ? (
          <div className="flex w-full justify-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void fetchNextPage()}
              disabled={isFetchingNextPage}
            >
              {isFetchingNextPage ? 'Loading…' : 'Show more'}
            </Button>
          </div>
        ) : null
      }
    />
  );
}
